import React, { useState } from 'react';
import styled from 'styled-components';
import { FiPlus, FiX, FiArrowUp } from 'react-icons/fi';
import { Button, Input, Textarea } from '../../ui';

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.4rem 0.3rem 0.7rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutral[100]};
  font-size: 0.85rem;

  button {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: ${({ theme }) => theme.colors.neutral[500]};
    &:hover { background: ${({ theme }) => theme.colors.neutral[200]}; }
  }
`;

const AddRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

interface TextListProps {
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  suggestions?: string[];
}

/** Lista de textos curtos (bairros, termos): Enter adiciona, X remove. */
export const TextListEditor: React.FC<TextListProps> = ({
  id, values, onChange, placeholder, suggestions = [],
}) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const value = draft.trim();
    if (value && !values.some((item) => item.toLowerCase() === value.toLowerCase())) {
      onChange([...values, value]);
    }
    setDraft('');
  };
  return (
    <div>
      {values.length > 0 && (
        <Chips>
          {values.map((value) => (
            <Chip key={value}>
              {value}
              <button
                type="button"
                aria-label={`Remover ${value}`}
                onClick={() => onChange(values.filter((item) => item !== value))}
              >
                <FiX size={13} />
              </button>
            </Chip>
          ))}
        </Chips>
      )}
      <AddRow>
        <Input
          id={id}
          value={draft}
          list={suggestions.length > 0 ? `${id}-sugestoes` : undefined}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" $variant="outline" onClick={add} aria-label="Adicionar">
          <FiPlus />
        </Button>
      </AddRow>
      {suggestions.length > 0 && (
        <datalist id={`${id}-sugestoes`}>
          {suggestions.map((item) => <option key={item} value={item} />)}
        </datalist>
      )}
    </div>
  );
};

const Pairs = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const Pair = styled.div`
  display: grid;
  gap: 0.5rem;
  padding: 0.85rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const PairTools = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
`;

interface PairListProps<T extends Record<string, string>> {
  items: T[];
  onChange: (items: T[]) => void;
  fields: { key: keyof T & string; placeholder: string; multiline?: boolean }[];
  addLabel: string;
  blank: T;
}

/** Lista de pares (destaques, perguntas): cada item editável, reordenável e removível. */
export function PairListEditor<T extends Record<string, string>>({
  items, onChange, fields, addLabel, blank,
}: PairListProps<T>) {
  const update = (index: number, key: keyof T, value: string) =>
    onChange(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...items];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };
  return (
    <Pairs>
      {items.map((item, index) => (
        <Pair key={index}>
          {fields.map((field) =>
            field.multiline ? (
              <Textarea
                key={field.key}
                rows={3}
                value={item[field.key]}
                placeholder={field.placeholder}
                onChange={(event) => update(index, field.key, event.target.value)}
              />
            ) : (
              <Input
                key={field.key}
                value={item[field.key]}
                placeholder={field.placeholder}
                onChange={(event) => update(index, field.key, event.target.value)}
              />
            )
          )}
          <PairTools>
            <Button type="button" $size="sm" $variant="ghost" onClick={() => moveUp(index)} disabled={index === 0}>
              <FiArrowUp /> Subir
            </Button>
            <Button
              type="button"
              $size="sm"
              $variant="ghost"
              onClick={() => onChange(items.filter((_, i) => i !== index))}
            >
              <FiX /> Remover
            </Button>
          </PairTools>
        </Pair>
      ))}
      <div>
        <Button type="button" $variant="outline" $size="sm" onClick={() => onChange([...items, { ...blank }])}>
          <FiPlus /> {addLabel}
        </Button>
      </div>
    </Pairs>
  );
}
